import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ArrowRight, CheckCircle2, Shield, Award, Users, Clock, Headphones, Building2, School, University, Briefcase, Star, Globe, Zap, Phone, Mail, MapPin } from '@/components/DeskeyIcons';

const BIGIN_GREEN = '#34A853';
const BIGIN_NAVY = '#1A365D';

// Deskey Logo Component
const DeskeyLogo = ({ size = 32, white = false }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.5 7.2C11.5 4.1 15 2.2 17.6 3.9L34.2 15.7C36.5 17.3 36.5 20.7 34.2 22.3L17.6 34.1C15 35.8 11.5 33.9 11.5 30.8V7.2Z" fill={white ? 'rgba(255,255,255,0.9)' : '#34A853'} />
  </svg>
);

export const PricingPage = () => {
  const navigate = useNavigate();
  const [selectedInstitution, setSelectedInstitution] = useState('School');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    institutionName: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.name || !formData.institutionName) {
      alert('Please fill in all required fields');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact-sales`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          institution_name: formData.institutionName,
          email: formData.email,
          phone: formData.phone,
          institution_type: selectedInstitution,
          message: formData.message,
          source: 'pricing_page'
        })
      });
      
      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({ name: '', email: '', phone: '', institutionName: '', message: '' });
      } else {
        alert('Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to submit. Please try again.');
    }
    setIsSubmitting(false);
  };

  const trustIndicators = [
    { icon: Shield, title: 'Enterprise Security', desc: 'SOC 2 compliant with end-to-end encryption' },
    { icon: Award, title: '99.9% Uptime', desc: 'Guaranteed availability with SLA' },
    { icon: Users, title: '500+ Institutions', desc: 'Trusted by schools and universities worldwide' },
    { icon: Clock, title: '24/7 Support', desc: 'Dedicated support team at your service' },
  ];

  const stats = [
    { value: '1M+', label: 'Students Learning' },
    { value: '50K+', label: 'Teachers Empowered' },
    { value: '10M+', label: 'Lessons Created' },
    { value: '35%', label: 'Better Outcomes' },
  ];

  const testimonials = [
    {
      quote: "Deskey transformed our entire teaching methodology. The ROI was visible within the first month.",
      author: "Dr. Priya Sharma",
      role: "Principal, Delhi Public School",
      avatar: "PS"
    },
    {
      quote: "Our students' engagement increased by 40% after implementing Deskey's AI-powered learning.",
      author: "Rajesh Kumar",
      role: "Director, Narayana Coaching",
      avatar: "RK"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <DeskeyLogo size={32} />
              <span className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif', color: BIGIN_NAVY }}>Deskey</span>
            </button>
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/login')} className="font-medium text-gray-700">
                Sign In
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-12 lg:py-20" style={{ background: `linear-gradient(135deg, ${BIGIN_GREEN}08, ${BIGIN_GREEN}15)` }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              
              {/* Left Side - Trust Building Content */}
              <div className="space-y-8">
                <div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: `${BIGIN_GREEN}15` }}>
                    <Zap className="w-4 h-4" style={{ color: BIGIN_GREEN }} />
                    <span className="text-sm font-semibold" style={{ color: BIGIN_GREEN }}>Transform Your Institution</span>
                  </div>
                  <h1 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Let's Build the Future of
                    <span className="block" style={{ color: BIGIN_GREEN }}> Education Together</span>
                  </h1>
                  <p className="text-lg text-gray-600 max-w-lg">
                    Get a customized demo and pricing tailored to your institution's needs. Our team will help you understand how Deskey can transform your educational outcomes.
                  </p>
                </div>

                {/* Trust Indicators */}
                <div className="grid grid-cols-2 gap-4">
                  {trustIndicators.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 bg-white rounded-2xl shadow-sm border border-gray-100">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${BIGIN_GREEN}15` }}>
                        <item.icon className="w-5 h-5" style={{ color: BIGIN_GREEN }} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm">{item.title}</h3>
                        <p className="text-xs text-gray-500">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-4 p-6 rounded-2xl" style={{ background: BIGIN_GREEN }}>
                  {stats.map((stat, i) => (
                    <div key={i} className="text-center">
                      <div className="text-2xl lg:text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{stat.value}</div>
                      <div className="text-white/70 text-xs">{stat.label}</div>
                    </div>
                  ))}
                </div>

                {/* Testimonials */}
                <div className="space-y-4">
                  {testimonials.map((testimonial, i) => (
                    <div key={i} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: BIGIN_GREEN }}>
                          {testimonial.avatar}
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-700 text-sm italic mb-2">"{testimonial.quote}"</p>
                          <div>
                            <div className="font-semibold text-gray-900 text-sm">{testimonial.author}</div>
                            <div className="text-gray-500 text-xs">{testimonial.role}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Side - Contact Form */}
              <div className="lg:sticky lg:top-24">
                <Card className="p-8 rounded-3xl shadow-xl border-0" style={{ 
                  background: 'white',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)'
                }}>
                  {submitSuccess ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 className="w-10 h-10 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Thank You!</h3>
                      <p className="text-gray-600 mb-6">Our sales team will contact you within 24 hours. Check your email for a welcome message with more information.</p>
                      <Button onClick={() => navigate('/')} className="rounded-full px-6" style={{ background: BIGIN_GREEN }}>
                        Back to Home
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Form Header */}
                      <div className="text-center mb-6">
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: `${BIGIN_GREEN}15` }}>
                          <Headphones className="w-7 h-7" style={{ color: BIGIN_GREEN }} />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Contact Sales</h2>
                        <p className="text-gray-500 text-sm">Get a personalized demo and pricing</p>
                      </div>

                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                            <Input 
                              placeholder="John Doe"
                              value={formData.name}
                              onChange={(e) => setFormData({...formData, name: e.target.value})}
                              className="h-11 border-gray-200 rounded-xl focus:border-[#34A853] focus:ring-2 focus:ring-[#34A853]/20"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                            <Input 
                              placeholder="+91 98765 43210"
                              value={formData.phone}
                              onChange={(e) => setFormData({...formData, phone: e.target.value})}
                              className="h-11 border-gray-200 rounded-xl focus:border-[#34A853] focus:ring-2 focus:ring-[#34A853]/20"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Work Email *</label>
                          <Input 
                            type="email"
                            placeholder="you@institution.edu"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            className="h-11 border-gray-200 rounded-xl focus:border-[#34A853] focus:ring-2 focus:ring-[#34A853]/20"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Institution Name *</label>
                          <Input 
                            placeholder="ABC School / University"
                            value={formData.institutionName}
                            onChange={(e) => setFormData({...formData, institutionName: e.target.value})}
                            className="h-11 border-gray-200 rounded-xl focus:border-[#34A853] focus:ring-2 focus:ring-[#34A853]/20"
                            required
                          />
                        </div>

                        {/* Institution Type */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Institution Type</label>
                          <div className="grid grid-cols-3 gap-2">
                            {[
                              { icon: School, name: 'School' },
                              { icon: Building2, name: 'Coaching' },
                              { icon: University, name: 'University' },
                            ].map((type, i) => (
                              <button 
                                key={i}
                                type="button"
                                onClick={() => setSelectedInstitution(type.name)}
                                className={`p-3 rounded-xl border-2 text-center transition-all duration-200 hover:scale-105 ${selectedInstitution === type.name ? 'text-white border-transparent shadow-md' : 'border-gray-100 hover:border-[#34A853]/40 bg-gray-50 hover:bg-white'}`}
                                style={{ background: selectedInstitution === type.name ? BIGIN_GREEN : undefined }}>
                                <type.icon className="w-5 h-5 mx-auto mb-1" style={{ color: selectedInstitution === type.name ? 'white' : '#6b7280' }} />
                                <span className="text-[10px] font-semibold block" style={{ color: selectedInstitution === type.name ? 'white' : '#6b7280' }}>{type.name}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Message (Optional)</label>
                          <textarea 
                            placeholder="Tell us about your requirements..."
                            value={formData.message}
                            onChange={(e) => setFormData({...formData, message: e.target.value})}
                            rows={3}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-[#34A853] focus:ring-2 focus:ring-[#34A853]/20 resize-none text-sm"
                          />
                        </div>

                        <Button 
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full h-12 font-bold rounded-xl text-white text-base shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.02] group disabled:opacity-50" 
                          style={{ background: BIGIN_GREEN }}>
                          {isSubmitting ? 'Submitting...' : 'Get Custom Quote'}
                          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </Button>

                        <p className="text-center text-xs text-gray-500">
                          By submitting, you agree to our Terms of Service and Privacy Policy
                        </p>
                      </form>
                    </>
                  )}
                </Card>

                {/* Contact Info */}
                <div className="mt-6 grid grid-cols-3 gap-4">
                  <a href="tel:+919876543210" className="flex items-center gap-2 p-3 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <Phone className="w-4 h-4" style={{ color: BIGIN_GREEN }} />
                    <span className="text-xs text-gray-600">Call Us</span>
                  </a>
                  <a href="mailto:sales@deskey.com" className="flex items-center gap-2 p-3 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                    <Mail className="w-4 h-4" style={{ color: BIGIN_GREEN }} />
                    <span className="text-xs text-gray-600">Email</span>
                  </a>
                  <div className="flex items-center gap-2 p-3 bg-white rounded-xl shadow-sm border border-gray-100">
                    <Globe className="w-4 h-4" style={{ color: BIGIN_GREEN }} />
                    <span className="text-xs text-gray-600">Global</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                What You Get with <span style={{ color: BIGIN_GREEN }}>Deskey</span>
              </h2>
              <p className="text-gray-600">Enterprise-grade features for modern education</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: '🎯', title: 'AI-Powered Lessons', desc: 'Automatic lesson generation from recorded classes' },
                { icon: '🌍', title: 'Multilingual Support', desc: 'Students learn in their preferred language' },
                { icon: '📊', title: 'Analytics Dashboard', desc: 'Track student progress and engagement' },
                { icon: '🔒', title: 'Data Security', desc: 'Enterprise-grade security and compliance' },
                { icon: '📱', title: 'Mobile Apps', desc: 'Native apps for teachers and students' },
                { icon: '🤝', title: 'Dedicated Support', desc: 'Onboarding and ongoing assistance' },
              ].map((feature, i) => (
                <div key={i} className="p-6 bg-gray-50 rounded-2xl hover:shadow-md transition-all">
                  <div className="text-3xl mb-3">{feature.icon}</div>
                  <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-black py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <DeskeyLogo size={28} white />
              <span className="text-white font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Deskey</span>
            </div>
            <p className="text-gray-500 text-sm">© 2025 Deskey. All rights reserved.</p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Privacy</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Terms</a>
              <a href="#" className="text-gray-400 hover:text-white transition-colors">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
